import { atom } from 'jotai';
import { FsagFormattedPreset } from '~/@types/calculator';
import { AvpConfigIds } from '~/app/market-config';
import { PossibleConfiguratorOptionValues } from '~/app/models/configurator-options';
import { RelevantRescueCodes } from '~/components/dialog/rescue-info-dialog';
import { CheckConflictsQuery, Incentive, VehicleType } from '~/graphql/__generated__/graphql';
import { ContentOrPackage, FeatureCategory, SelectableOption } from '~/graphql/types';
import { FeatureLoadingMap } from './models/feature-loading-map';
import { NgcCarIdentifier } from '~/app/utils/graphql-sub-types';

export const isBodyNoneScroll = atom(false);
export const isLandscape = atom(false);
export const currentView = atom<'configurator' | 'summary' | 'error'>('configurator');
export const carType = atom<VehicleType>();
export const entryType = atom<'audicode' | 'pr string' | 'default'>('default');
export const messageDialog = atom<{ title: string; message: string } | null>(null);
export const termsAndConditionsDialog = atom<{
	incentive: Incentive;
	onSelect: (incentive: Incentive) => void;
	showDialogButtons: boolean;
} | null>(null);

export const moreInformationDialogData = atom<{
	open: boolean;
	item: SelectableOption<FeatureCategory> | undefined;
	featureOrPackage: ContentOrPackage | undefined;
	hideAddConfigButton: boolean;
	hidePriceAndAvailability: boolean;
	trackSelection?: () => void;
	step?: number;
	total?: number;
	category?: string;
} | null>(null);
export const iframeDialog = atom<{ url: string; name: string } | null>(null);
export const configuredCombinationsAtom = atom<PossibleConfiguratorOptionValues[] | undefined>([]);
export const filteredCombinationsAtom = atom<PossibleConfiguratorOptionValues[] | undefined>([]);
export const hiddenConfiSteps = atom<Record<PossibleConfiguratorOptionValues, boolean> | undefined>(undefined);
export const active3DViews = atom<string[]>([]);
export const AVPSessionId = atom('');
export const AVPConfigId = atom<AvpConfigIds>('a-ngc-default-my2025');
export const AVPCameraId = atom<string>('MRV1');
export const isDebugMode = atom(false);

// The consumption & emission disclaimers are delivered in GQL in the trimlines.drivetrain object
// after loading the drivetrain, we store them here
export const consumptionDisclaimersAtom = atom<string[]>([]);
export const consumptionDisclaimersConfiguredCarAtom = atom<string[]>([]);

export const similarCarSearchArea = atom<{ radius: number; latitude: number; longitude: number } | undefined>(
	undefined,
);

export function getInitialLoadingStateMap(): FeatureLoadingMap {
	return {
		trimline: {},
		driveTrain: {},
		rims: {},
		steeringWheel: {},
		seat: {},
		interiorTile: {},
		headlight: {},
		bodyPaint: {},
		exteriorPaintType: {},
		accessory: {},
		package: {},
		packageFeature: {},
		interiorPackage: {},
		incentive: {},
	};
}

export const activeFilters = atom<Record<string, Array<string>>>({
	rims: [],
	bodyPaint: [],
	seats: [],
	seatCovers: [],
});

export const rescueInfoAdjustments = atom<{
	adjustments: RelevantRescueCodes[];
	urlAudicode: string;
}>({
	adjustments: [],
	urlAudicode: '',
});

export const lastTrigger = atom<string | null>(null);

export const hasConfigChanged = atom<boolean>(false);

export const hasFinancingChanged = atom<boolean>(false);

export const hasAudiCodeChanged = atom<boolean>(false);

export const audicodeIdentifier = atom<string>('');

export const audicodeFinancing = atom<string>('');

export const financialProducts = atom<Array<{ key: string; val: string }>>([]);

export const financialError = atom<boolean>(false);

export const defaultFinancialProduct = atom<string | undefined>(undefined);

export const financingDisclaimerText = atom<string>('');

export const fsagFinancialCalculatorSettings = atom<FsagFormattedPreset | undefined>(undefined);

export const loadingFeatureIdsAtom = atom<FeatureLoadingMap>(getInitialLoadingStateMap());

export const conflictAtom = atom<CheckConflictsQuery['change'] | null>(null);

export const defaultIdentifier = atom<(NgcCarIdentifier | undefined)[] | undefined>([]);

export const dialogs = atom({
	audiCode: false,
	technicalData: false,
	standardEquipment: false,
	moreInformation: false,
	unauthenticated: false,
	featureUnavailable: false,
	priceDetails: false,
	monthlyPriceRatesNO: false,
	monthlyPriceRatesFsag: false,
	galleryImages: false,
	stockCarMatchingResults: false,
	iframe: false,
	responseNotification: false,
	rescueInfo: false,
});
